import React, { Fragment } from "react";
import FormUtils from "../../utils/FormUtil";
import FormContext from "../form-context";
import { MSGS } from "../../constants";
import ValidationUtil from "../../utils/ValidationUtil";
import { IField, IMeta, IOption } from "../../constants/model-interfaces";
import { IError, IRenderField } from "../../constants/common-interface";
export default abstract class BaseFormControl extends React.Component {
    static contextType = FormContext;
    context!: React.ContextType<typeof FormContext>;
    displayType: string | undefined;
    field: IField;
    isFormControl: boolean;
    uuid: string;
    section: string;
    validation: { required: boolean | undefined; pattern: string | undefined };
    state: IState;

    constructor(public props: IRenderField) {
        super(props);
        this.field = props;
        this.displayType = props.meta.displayType;
        this.section = props.section;
        this.isFormControl = FormUtils.isFormControl(props.meta);
        this.uuid = FormUtils.getUUID();
        this.validation = {
            required: props?.meta?.validation?.required ? true : undefined,
            pattern: props?.meta?.validation?.pattern ? props?.meta?.validation?.pattern : undefined
        };
        this.state = {
            error: { hasError: false, errorMsg: "" },
            componentError: { hasError: false, errorMsg: "" },
            form: this.props.form,
            loading: false
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
    }

    setLoading(loading: boolean) {
        this.setState({ loading });
    }

    initConfig() {
        const configData = this.props.form?.config || null;
        let initData = this.props.form?.init || null;
        if (configData) {
            // check init mode
            const hasInitCode = configData?.loadOn && configData.loadOn.indexOf("init") >= 0;
            if (hasInitCode) {
                initData = configData;
            } else {
                // check edit mode
                if (this.props.form.value) {
                    // checking now for string only
                    initData = configData;
                }
            }
        }
        if (initData) {
            if (!(initData?.type === "url" || initData?.type === "url_loader" || initData?.type === "options_loader")) {
                return;
            }
            this.setLoading(true);
            this.context
                .getData(initData, this.props.form.value, this.props.section, "$initial")
                .then((options: Array<IOption>) => {
                    this.setLoading(false);
                    this.context.setFieldOptions(this.section, this.field.name, options);
                    if (this.props.form.value !== undefined) {
                        const ref = options.find((option) => option.value === this.props.form.value);
                        if (ref) {
                            this.handleChange(null, this.props.form.value, ref);
                        }
                        this.props.sync();
                    }
                })
                .catch(() => {
                    this.setLoading(false);
                });
        }
    }

    componentDidUpdate(props: any) {
        // no validation for non-displayed fields
        if (props && props?.form?.display) {
            const isDisabled = props.form?.isDisabled;
            if (isDisabled) {
                // reset disabled fields
                if (this.state.error.hasError) {
                    // eslint-disable-next-line react/no-did-update-set-state
                    this.setState({
                        error: {
                            hasError: false,
                            errorMsg: ""
                        }
                    });
                }
            } else if (this.state.error.hasError !== this.props.form.error?.hasError) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({
                    error: {
                        hasError: this.props.form.error?.hasError,
                        errorMsg: this.props.form.error?.errorMsg
                    }
                });
            }
        }
    }

    componentDidCatch(error: any, errorInfo: any) {
        this.setState({
            componentError: { hasError: true, errorMsg: error.message }
        });
    }

    componentDidMount() {
        this.initConfig();
    }

    render() {
        if (this.isFormControl) {
            return this.state.componentError.hasError ? (
                <div className='w-100 h-100 text-center d-flex align-items-center justify-content-center text-danger'>{this.state.componentError.errorMsg}</div>
            ) : (
                <Display form={this.props.form}>{this.control()}</Display>
            );
        }
        return <Fragment />;
    }

    control() {
        const displayType = this.props.form?.displayType ?? this.displayType;
        switch (displayType) {
            case "header":
                return this.header();
            case "paragraph":
                return this.paragraph();
            case "label":
                return this.label();
            case "month":
                return this.month();
            case "date":
                return this.date();
            case "select":
                return this.select();
            case "radio":
                return this.radio();
            case "radio-button":
                return this.radioButton();
            case "checkbox":
                return this.checkbox();
            case "email":
                return this.email();
            case "number":
                return this.number();
            case "phone":
                return this.phone();
            case "password":
                return this.password();
            case "search":
                return this.search();
            case "hint":
                return this.hint();
            case "modalsearch":
                return this.modalsearch();
            case "file":
                return this.file();
            case "multitext":
                return this.multitext();
            case "text":
            case "text_field":
                return this.text();
            case "template":
                return this.templateControl();
            case "text_custom":
                return this.customTextControl();
            case "currency":
                return this.currency();
            default:
                // use custom control
                return this.customControl();
        }
    }

    abstract month(): JSX.Element;
    abstract phone(): JSX.Element;
    abstract date(): JSX.Element;
    abstract search(): JSX.Element;
    abstract text(): JSX.Element;
    abstract label(): JSX.Element;
    abstract password(): JSX.Element;
    abstract email(): JSX.Element;
    abstract number(): JSX.Element;
    abstract radio(): JSX.Element;
    abstract checkbox(): JSX.Element;
    abstract select(): JSX.Element;
    abstract multiselect(): JSX.Element;
    abstract button(): JSX.Element;
    abstract hint(): JSX.Element;
    abstract file(): JSX.Element;
    // non-mandatory
    radioButton(): JSX.Element {
        throw Error("Not implemented");
    }

    multitext(): JSX.Element {
        throw Error("Not implemented");
    }

    header() {
        return <h2 className={this.getWrapperClassName()}>{this.props.form.displayName}</h2>;
    }

    modalsearch(): JSX.Element {
        throw Error("Not implemented");
    }

    paragraph(): JSX.Element {
        return <p className={this.getWrapperClassName()}>{this.props.form.displayName}</p>;
    }

    customControl(): JSX.Element {
        if (this.displayType) {
            const customWrapperClass = this.getWrapperClassName();
            return <div className={customWrapperClass}>{this.context.getControl(this.displayType)}</div>;
        }
        return this.text();
    }

    templateControl(): JSX.Element {
        throw Error("Not Implemented");
    }

    customTextControl(): JSX.Element {
        throw Error("Not Implemented");
    }

    currency(): JSX.Element {
        throw Error("Not Implemented");
    }

    handleChange(e: any, val?: any, ref?: any) {
        try {
            const value = val !== undefined ? val : e.target.value;
            this.context.setField(this.section, this.field.name, value);
            this.context.handleChangeEvents(this.section, this.field.name, value, ref);
            this.handleDependencies(value);
            this.context.emit("$field_change", {
                section: this.section,
                field: this.field.name,
                value
            });
        } catch (error) {
            console.error(error);
        }
    }

    handleOpen(e: any) {
        const field = this.context.getField(this.section, this.field.name);
        const eventItem = field?.events?.open || field.config;
        switch (eventItem.type) {
            case "url":
            case "options_loader":
                // eslint-disable-next-line no-lone-blocks
                {
                    this.context
                        .api("get", eventItem.url, eventItem.queryParams, "", this.props.section)
                        .then((response: any) => {
                            let options = eventItem.responseKey ? response[eventItem.responseKey] : response;
                            const labelKey = eventItem.labelKey || "label";
                            const valueKey = eventItem.valueKey || "value";
                            if (options) {
                                options = options.map((o: any) => ({
                                    label: o[labelKey],
                                    value: o[valueKey],
                                    ref: o
                                }));
                            }
                            this.context.setFieldOptions(this.section, this.field.name, options);
                            this.setState({ form: { ...this.props.form } });
                        })
                        .catch((error: any) => {
                            this.context.handleError(error, this.section, this.field.name);
                            this.setState({ form: { ...this.props.form } });
                        });
                }
                break;
        }
    }

    handleDependencies(value: any) {
        this.props.sync();
        this.context.handleDependencies(this.section, this.field.name, value, true).finally(() => {
            this.props.sync();
        });
    }

    getWrapperClassName() {
        const customClassName = this.props.meta?.className || "";
        return (
            "meta-form-control-" +
            this.field.name +
            (this.props.form.isReadonly ? " meta-readonly" : "") +
            (" meta-form-control-" + this.displayType) +
            (customClassName ? " " + customClassName : "")
        );
    }

    handleValidation() {
        const value = this.props.form.value;
        this.validate(value);
    }

    validate(value: string | number | boolean | undefined) {
        const meta = this.props.form;
        if (meta.validation?.required) {
            const isEmpty = ValidationUtil.isEmptyField(value);
            if (isEmpty) {
                const errorMsg =
                    // eslint-disable-next-line camelcase
                    meta.validation?.required_detail?.errorMsg || meta.validation?.requiredDetail?.errorMsg || MSGS.ERROR_MSG.REQUIRED;
                this.setError(true, errorMsg);
                return;
            }
        }
        if (meta.validation?.pattern) {
            const regx = new RegExp(meta.validation?.pattern);
            const strValue = value ? value + "" : "";
            if (value && !regx.test(strValue)) {
                const errorMsg =
                    // eslint-disable-next-line camelcase
                    meta.validation?.pattern_detail?.errorMsg || meta.validation.patternDetail?.errorMsg || MSGS.ERROR_MSG.PATTERN;
                this.setError(true, errorMsg);
                return;
            }
        }
        if (meta.validation?.min) {
            const hasError = ValidationUtil.updateMinError(meta, value, this.setError.bind(this));
            if (hasError) return;
        }
        if (meta.validation?.max) {
            const hasError = ValidationUtil.updateMaxError(meta, value, this.setError.bind(this));
            if (hasError) return;
        }
        this.setError(false, "");
    }

    setError(hasError: boolean, errorMsg: string) {
        const error = { hasError, errorMsg };
        this.context.setError(this.section, this.field.name, error);
        this.setState({ error });
    }

    getDisplayLabel() {
        return this.props.form.validation?.required ? this.props.form.displayName + " *" : this.props.form.displayName;
    }
}

function Display(props: any) {
    return <Fragment>{props?.form?.display && props.children}</Fragment>;
}

interface IState {
    error: IError;
    componentError: IError;
    form: IMeta;
    loading: boolean;
}
